import { PhotoAgrandie } from '../../components/PhotoAgrandie.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

const ETAPES = { avant: '🧱', pendant: '🔨', apres: '✨', probleme: '⚠' };

/** Du plus récent au plus ancien : ce qu'on cherche est souvent récent. */
const recentsDabord = (a, b) =>
  String(b.date || '').localeCompare(String(a.date || '')) ||
  String(b.h || '').localeCompare(String(a.h || ''));

/** Les dates du classeur arrivent en ISO ou déjà en suisse. */
const lisible = (d) => (/^\d{4}-\d{2}-\d{2}/.test(String(d || '')) ? dateSuisse(d) : d || '');

/**
 * Le classeur des chantiers : pour chacun, son nom, son client et son adresse,
 * puis tous les rapports de l'équipe et toutes les photos prises.
 *
 * <p>La tuile « Chantiers & rapports » ne donnait que le nom du chantier. En
 * cas de litige, le patron choisit le chantier et retrouve ici chaque rapport
 * et chaque photo — d'avant, de pendant, d'après, de problème, et celles
 * montrées au client.
 *
 * @param {object} props propriétés
 * @param {object[]} props.chantiers chantiers de la société
 * @param {object[]} props.clients clients, pour le nom du client
 * @param {object} props.styleGroupe style des titres de groupe du classeur
 * @returns {import('react').ReactElement} les chantiers du classeur
 */
export function ClasseurChantiers({ chantiers, clients, styleGroupe }) {
  const liste = [...(chantiers || [])].sort((a, b) =>
    String(a.nom || '').localeCompare(String(b.nom || '')),
  );
  return (
    <>
      <div style={styleGroupe}>{tr('🏗️ Chantiers — rapports et photos')}</div>
      {liste.length === 0 && (
        <Card>
          <div style={{ padding: 14, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            {tr('Aucun chantier pour l’instant.')}
          </div>
        </Card>
      )}
      {liste.map((ch) => {
        const client = (clients || []).find((c) => c.id === ch.clientId);
        const adresse = [ch.adr, ch.npa].map((m) => String(m ?? '').trim()).filter(Boolean);
        const rapports = [...(ch.rapports || [])].sort(recentsDabord);
        const photos = [...(ch.photos || [])].sort(recentsDabord);
        return (
          <Card key={ch.id} style={{ marginBottom: 6, overflow: 'hidden' }}>
            <details data-testid="classeur-chantier">
              <summary
                style={{
                  padding: '12px 14px',
                  cursor: 'pointer',
                  listStyle: 'none',
                  display: 'flex',
                  gap: 11,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 11,
                    background: C.navyXL,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  🏗️
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800 }}>{ch.nom || ch.code}</div>
                  <div style={{ fontSize: 10, color: C.grey }}>
                    {[client?.nom, adresse.join(', ')].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
                    {trf('{0} rapport(s) · {1} photo(s)', rapports.length, photos.length)}
                    {ch.statut ? ' · ' + ch.statut : ''}
                  </div>
                </div>
                <div style={{ fontSize: 10, fontWeight: 800, color: C.navy }}>▸</div>
              </summary>
              <div style={{ padding: '0 14px 12px' }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: 'uppercase',
                    margin: '4px 0 6px',
                  }}
                >
                  {tr('📋 Rapports')}
                </div>
                {rapports.length === 0 && (
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>
                    {tr('Aucun rapport.')}
                  </div>
                )}
                {rapports.map((r, i) => (
                  <div
                    key={r.id ?? i}
                    style={{
                      borderTop: '1px solid ' + C.border,
                      padding: '7px 0',
                      fontSize: 10.5,
                    }}
                  >
                    <div style={{ fontWeight: 800, color: C.navy }}>
                      {lisible(r.date)}
                      {r.empNom ? ' · ' + r.empNom : ''}
                      {r.meteo ? ' · ' + r.meteo : ''}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', color: C.ink, marginTop: 2 }}>
                      {r.contenu}
                    </div>
                  </div>
                ))}
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: C.muted,
                    textTransform: 'uppercase',
                    margin: '10px 0 6px',
                  }}
                >
                  {tr('📷 Photos')}
                </div>
                {photos.length === 0 && (
                  <div style={{ fontSize: 10, color: C.muted }}>{tr('Aucune photo.')}</div>
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 6,
                  }}
                >
                  {photos.map((p, i) => (
                    <div key={p.id ?? i} style={{ minWidth: 0 }}>
                      <div
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: C.navyXL,
                          border: '2px solid ' + (p.cat === 'probleme' ? C.red : 'transparent'),
                        }}
                      >
                        {p.src ? (
                          <PhotoAgrandie src={p.src} alt={p.note || p.label || ''} />
                        ) : (
                          <span style={{ fontSize: 22 }}>{p.emoji}</span>
                        )}
                        <span
                          style={{
                            position: 'absolute',
                            top: 3,
                            left: 3,
                            fontSize: 11,
                            background: 'rgba(255,255,255,.85)',
                            borderRadius: 5,
                            padding: '0 3px',
                          }}
                        >
                          {ETAPES[p.cat] || '📷'}
                          {p.partageClient ? ' 👁' : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: 8.5, color: C.grey, marginTop: 2, lineHeight: 1.3 }}>
                        {lisible(p.date)}
                        {p.auteur ? ' · ' + p.auteur : ''}
                        {p.poste ? <div style={{ color: C.muted }}>{p.poste}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          </Card>
        );
      })}
    </>
  );
}
